import InteractionLinestringEntity from "../interaction/InteractionLinestringEntity";
import InteractionPointEntity from "../interaction/InteractionPointEntity";
import LinestringEntity from "../edit/LinestringEntity";
import PointEntity from "../edit/PointEntity";
import InteractionPolygonEntity from "../interaction/InteractionPolygonEntity";
import PolygonEntity from "../edit/PolygonEntity";
import DitapPopup from "../popup/DitapPopup";
import DitapMask from "../popup/DitapMask";

/**
 * 엔티티 생성을 위한 도구 모음 팝업. DitapViewer 내부적으로 생성되어 사용하는 팝업.</br>
 * Entity creation toolset popup. The popup used internally by DitapViewer.
 *
 * @alias DrawingTypePicker
 * @constructor
 *
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {object[]} createObjArray 표시 할 생성 도구 배열.</br> Array containing shape creation tools to display.
 *
 * @example
 * // 1. DitapViewer 생성 시 DrawingTypePicker 생성 가능.
 * // 단독으로 사용 불가하며 편집 기능에 담아서 사용 가능.
 * // editingTools안에 createTools에 사용할 생성 도구 작성.
 * // 활성화 - 전체 활성화(createTools: true) 또는 부분 활성화(활성화 시킬 기능만 true)
 * // 비활성화 - false 또는 전달 안함
 * const viewer = new Ditap.DitapViewer("container", {
 *    editingTools: {
 *        createTools: {
 *            point: true,
 *            linestring: true,
 *            polygon: true,
 *        },
 *    }
 * });
 *
 * // 단독으로 선언할 경우(EditToolPicker 선언하여 매개변수로 전달)
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 * // 사용할 도형 생성 도구 모음 초기화
 * const createObjArray = [
 *    new Ditap.InteractionPointEntity(),
 *    new Ditap.InteractionLinestringEntity(),
 *    new Ditap.InteractionPolygonEntity(),
 * ];
 *
 * // DrawingTypePicker 생성
 * const drawingTypePicker = new Ditap.DrawingTypePicker(
 *    viewer,
 *    createObjArray
 * );
 *
 * // EditToolPicker 선언 후 매개변수로 전달
 * const editToolPicker = new Ditap.EditToolPicker(viewer, [drawingTypePicker]);
 */
class DrawingTypePicker extends DitapPopup {
  constructor(viewer, createObjArray) {
    const parent = viewer.container.querySelector(".cesium-viewer")
      ? viewer.container.querySelector(".cesium-viewer")
      : viewer.container;
    super(parent);

    // 전체 생성
    if (createObjArray === undefined || createObjArray.length === 0) {
      createObjArray = [
        new InteractionPointEntity(),
        new InteractionLinestringEntity(),
        new InteractionPolygonEntity(),
        // "hexahedron",
        // "sphere",
        // "cylinder",
      ];
    }

    // 박스 생성
    const drawingTypePickerBox = this.createDrawingTypePickerBox(
      createObjArray,
      viewer
    );

    // 헤더 추가
    this.prependHeader(drawingTypePickerBox["header"]["title"]);

    // 컨텐츠 추가
    this.appendSubTitle(drawingTypePickerBox["content"]["subTitle"]);
    this.appendContent(drawingTypePickerBox["content"]["selectList"]);

    this.footer.remove();

    this._currentEvent = undefined;
    this._typeButton = undefined;
    this._clickEvent = undefined;
    this._mask = new DitapMask();
  }

  /*
   * 버튼 클릭 시 실행 이벤트</br>
   * Event to run when button clicked.
   * @type {Function}
   */
  set currentEvent(currentEvent) {
    this._currentEvent = currentEvent;
  }
  get currentEvent() {
    return this._currentEvent;
  }

  /*
   * 이벤트 할당 버튼 요소.</br>
   * Button to assign events.
   * @type {Element}
   */
  set typeButton(typeButton) {
    this._typeButton = typeButton;
  }
  get typeButton() {
    return this._typeButton;
  }

  /*
   * 버튼 클릭 시 실행 이벤트</br>
   * Events that run when a button is clicked
   * @type {Function}
   */
  set clickEvent(clickEvent) {
    this._clickEvent = clickEvent;
  }
  get clickEvent() {
    return this._clickEvent;
  }

  /*
   * 컨테이너에 생성할 도형 버튼 추가.</br>
   * Add a shape button to be created in the container.
   * @param {object[]} createObjArray 표시 할 생성 도구 배열.</br> Array containing shape creation tools to display.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @returns {object} DOM 요소를 포함하는 객체.</br> Objects containing Dom elements
   */
  createDrawingTypePickerBox(createObjArray, viewer) {
    const wrapper = this.wrapper;
    // wrapper
    wrapper.classList.add("ditap-drawing-popup");

    // 헤더
    const title = document.createElement("h2");
    title.classList.add("ditap-popup-title");
    title.innerText = "도형선택";

    // 컨텐츠
    const subTitle = document.createElement("p");
    subTitle.classList.add("ditap-popup-subtitle");
    subTitle.innerText = "아래의 도형을 선택해주세요.";

    const selectContainer = document.createElement("div");
    selectContainer.classList.add("ditap-drawing-popup-container");

    const selectList = document.createElement("ul");
    selectList.classList.add(
      "ditap-drawing-popup-list",
      "ditap-drawing-popup-small-list"
    );

    for (const obj of createObjArray) {
      let text;
      let type;
      let event = this.currentEvent;
      if (obj instanceof InteractionPointEntity) {
        text = "점";
        type = "point";
        event = () => {
          // 기존에 실행된 인터렉션 있을 경우 종료
          viewer.offAllInterations();

          const pointEntity = new PointEntity(viewer);
          obj.pointEntity = pointEntity;
          obj.on();
        };
      } else if (obj instanceof InteractionLinestringEntity) {
        text = "선";
        type = "line";
        event = () => {
          // 기존에 실행된 인터렉션 있을 경우 종료
          viewer.offAllInterations();

          const linestringEntity = new LinestringEntity(viewer);
          obj.linestringEntity = linestringEntity;
          obj.on();
        };
      } else if (obj instanceof InteractionPolygonEntity) {
        text = "면";
        type = "polygon";
        event = () => {
          // 기존에 실행된 인터렉션 있을 경우 종료
          viewer.offAllInterations();

          const polygonEntity = new PolygonEntity(viewer);
          obj.polygonEntity = polygonEntity;
          obj.on();
        };
      }
      // else if (obj === "hexahedron") {
      //   text = "육면체";
      //   type = "hexahedron";
      // } else if (obj === "sphere") {
      //   text = "구";
      //   type = "sphere";
      // } else if (obj === "cylinder") {
      //   text = "원통";
      //   type = "cylinder";
      // }

      const li = document.createElement("li");
      const button = document.createElement("button");
      const p1 = document.createElement("p");
      const p2 = document.createElement("p");

      li.classList.add("ditap-drawing-popup-item");
      button.classList.add("ditap-btn-reset");
      button.classList.add(`ditap-${type}-btn`);
      p1.classList.add("ditap-drawing-img");
      p2.classList.add("ditap-drawing-title");
      p2.textContent = text;
      button.appendChild(p1);
      button.appendChild(p2);
      li.appendChild(button);
      selectList.appendChild(li);
      selectContainer.appendChild(selectList);

      this._typeButton = li;

      li.addEventListener(
        "click",
        (this.clickEvent = () => {
          this.close();
          event();
        })
      );
    }

    // // 전체 보기 버튼
    // const div4 = document.createElement("div");
    // const p2 = document.createElement("p");
    // const button2 = document.createElement("button");
    // div4.classList.add("ditap-popup-footer");
    // p2.classList.add("ditap-popup-subtitle");
    // p2.textContent = "더 많은 도형을 보려면";
    // button2.classList.add("highlight");
    // button2.classList.add("ditap-btn-reset");
    // button2.textContent = "전체보기";
    // p2.appendChild(button2);
    // p2.append("를 눌러주세요");
    // div4.appendChild(p2);

    // div1.appendChild(div3);

    return {
      header: {
        title: title,
      },
      content: {
        subTitle: subTitle,
        selectList: selectContainer,
      },
    };
  }

  /*
   * 실행중인 이벤트 제거.</br>
   * Removes running events.
   */
  removeClickEvent() {
    if (this.currentEvent) {
      this.typeButton.removeEventListener("click", this.clickEvent);
    }
  }

  /*
   * 생성 도구 모음 컨테이너 생성.</br>
   * Created toolset container for creation.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @return {Element} 생성된 컨테이너 DOM 요소.</br> Created container DOM element.
   */
  createDrawingTypePickerContainer(viewer) {
    // wrapper
    const drawingContainer = document.createElement("div");
    drawingContainer.classList.add("ditap-wrap", "js-ditap-wrap");

    viewer.container.querySelector(".cesium-viewer").append(drawingContainer);

    return drawingContainer;
  }

  /*
   * 팝업 열기.</br>
   * Open popup.
   * @param {Element} element 매개변수가 없는 경우 wrapper 오픈.</br> element If no parameter is entered, open wrapper.
   */
  open(element) {
    super.open(element);
    this._mask.openMobile();
  }

  /*
   * 팝업 닫기.</br>
   * Close popup.
   * @param {Element} element 매개변수가 없는 경우 wrapper 오픈.</br>  If no parameter is entered, close wrapper.
   */
  close(element = undefined) {
    this._mask.closeMobile();
    super.close(element);
  }

  /**
   * 이 객체의 모든 리소스 제거.</br>
   * Remove all resources of this object.
   */
  destroy() {
    this._mask.closeMobile();
    super.destroy();
  }
}

export default DrawingTypePicker;
